<template>
  <div style="margin-top: 60px" class="home">
    <Navbar :cartIsVisible="false"></Navbar>

    <div id="success-section" class="success-section">
      <h1 class="section-title" :style="{ color: sectionTitleColorTheme }">
        {{ $t("payment.yourOrderIsInProgress") }}
        <br />
        <br />{{ $t("payment.itWillReachYou") }}
        <span class="tableQR-header"
          >{{ $t("payment.desk") }} {{ tableQRLabel(tableQR) }}</span
        >
      </h1>
    </div>
    <SendEmailModal
      ref="sendEmailModal"
      :isPaid="isPaid"
      :paymentType="paymentType"
      :tableQR="tableQR"
      :paymentMethod="paymentMethod"
    ></SendEmailModal>
    <table-choooser-dialog
      ref="tableChooserDialog"
      @submit="processPayment"
    ></table-choooser-dialog>
  </div>
</template>

<script>
import { getPaymentById, updatePayment } from "@/collections/payments";
import { firestoreTimestamp } from "@/firebaseConfig";
import Navbar from "@/components/public/common/Navbar";
import firebaseMixin from "@/mixins/firebase/firebaseMixin";
import SuccessMixin from "@/mixins/success/SuccessMixin";
import SendEmailModal from "@/components/public/modals/SendEmailModal";
import TableChoooserDialog from "../../../../components/public/modals/TableChoooserDialog.vue";
import { getQrZoneById } from "@/collections/qrZone";
import { saveOrder } from "@/collections/orders";
import { getQRByTable } from "@/collections/qrs";
import { deleteAllUserCartByTable } from "@/collections/userCart";
const NO_QR = "NO QR";

export default {
  name: "Success",
  mixins: [firebaseMixin, SuccessMixin],
  props: ["sessionId"],
  components: {
    Navbar,
    SendEmailModal,
    TableChoooserDialog
  },
  data() {
    return {
      orderCreated: false,
      total: 0,
      isPaid: false, // should pay or request
      paymentType: "product",
      paymentMethod: "credit_card",
      canBeDirect: false,
      lastOrder: this.$store.state.cart
    };
  },
  computed: {
    homeUrl() {
      return `/?qr=${this.tableQR}`;
    },

    fromCheckout() {
      return window.localStorage.getItem("lastLand") === "checkout";
    },
    isValidSessionId() {
      return this.sessionId !== "_";
    },
    paymentId() {
      return this.$router.currentRoute.query.p_id;
    }
  },
  created() {
    // if the cart is empty and the user doesn't come from the checkout, redirect to home
    if (this.$store.state.cart.length === 0 && !this.fromCheckout) {
      this.$router.replace(this.homeUrl);
    }
  },
  async mounted() {
    if (this.isValidSessionId) {
      // save payment method to localstorage
      this.savePaymentMethod();
    }

    if (this.tableQR === NO_QR) {
      this.$refs.tableChooserDialog.open();
      return;
    }

    this.processPayment();
  },
  methods: {
    async processPayment() {
      try {
        if (!this.paymentId) {
          // save order as requested
          await this.saveSuccessOrder();
        } else {
          let payment = await getPaymentById(this.paymentId);

          // check if payment already marked as success
          if (payment && payment.success) {
            this.$router.replace(this.homeUrl);
            return;
          }

          this.canBeDirect = await this.isDirectPayment();

          // variable for if user want to pay
          this.paymentType = localStorage.getItem("payment_type"); // "product" or "amount"

          if (this.canBeDirect) {
            this.handleDirectPayment();
          } else {
            // deduct amount from remaining to pay
            await this.updateOrderTotalByTable();
            if (this.paymentType === "product") {
              // set is paid to true in particular order which is selected in ProductChooserDialog
              this.setIsPaid();
            }
          }
        }
      } catch (error) {
        this.setlogError(
          "stripe-payment",
          "Obtained error message",
          error.stack,
          this.$options.name
        );
      }
    },
    async isDirectPayment() {
      try {
        let table = await getQRByTable(this.tableQR);
        if (table) {
          let zoneQR = await getQrZoneById(table.zone_id);
          return zoneQR ? zoneQR.acceptDirectPayment : false;
        }
        return false;
      } catch (error) {
        // eslint-disable-next-line
        this.setlogError(
          "get/qr_by_table/qr_zone_by_id",
          "Obtained error message in isDirectPayment()",
          error.stack,
          this.$options.name
        );
        return false;
      }
    },
    async savePaymentMethod() {
      // get session detail from session id
      let session = await this.$api.stripe.getSession(this.sessionId);

      // get public intent from session detail
      let paymentIntent = await this.$api.stripe.getPaymentIntent(
        session.payment_intent
      );

      // get public method from public intent
      let paymentMethod = await this.$api.stripe.getPaymentMethod(
        paymentIntent.payment_method
      );

      // attach public method to customer
      // eslint-disable-next-line no-unused-vars
      await this.$api.stripe.attachPaymentMethod(paymentIntent.payment_method, {
        customer: paymentIntent.customer
      });

      // get customer details and store to the localstorage
      let customer = await this.$api.stripe.getCustomer(paymentIntent.customer);

      this.$store.commit("addPaymentMethod", {
        email: customer.email,
        paymentMethod: paymentMethod.id,
        customerId: customer.id,
        cardLast4: paymentMethod.card.last4
      });

      let localPaymentMethods = JSON.parse(
        localStorage.getItem("paymentMethods") || "[]"
      );

      let found = localPaymentMethods.find(
        x => x.cardLast4 === paymentMethod.card.last4
      );

      if (!found) {
        localPaymentMethods.push({
          email: customer.email,
          paymentMethod: paymentMethod.id,
          customerId: customer.id,
          cardLast4: paymentMethod.card.last4
        });
        localStorage.setItem(
          "paymentMethods",
          JSON.stringify(localPaymentMethods)
        );
      }
    },
    async handleDirectPayment() {
      let self = this;
      try {
        if (this.orderCreated === false && this.fromCheckout) {
          let time = firestoreTimestamp.fromDate(new Date());
          let orders = [...this.cart];
          this.cart.forEach(item => {
            let id = self.uuidv4();
            saveOrder(id, self.buildOrder(item, time, id));

            self.total += item.price * item.quantity;
          });

          // set total order quantity in localstorage
          this.saveMyOrders();

          // save general comments in firebase
          this.saveGeneralComment(time);

          this.showSuccessMessage(this.$t("payment.paymentMade"));

          this.orderCreated = true;

          // create order user log
          this.setlogInfo(
            "create_order",
            "Pedido creado",
            [...this.cart],
            this.$options.name
          );

          // Update success: true to prevent using same URL to make other orders
          await updatePayment(this.paymentId, {
            success: true
          });

          // clear everything from local storage
          this.$store.commit("removeAllCart");
          await deleteAllUserCartByTable(this.tableQR, false);
          window.localStorage.setItem("lastLand", "");

          this.$refs.sendEmailModal.openDialog(
            orders,
            self.total,
            this.canBeDirect
          );
        }
      } catch (error) {
        this.setlogError(
          "update_payment",
          "Obtained error message in handleDirectPayment()",
          error.stack,
          this.$options.name
        );
      }
    },
    async saveSuccessOrder() {
      try {
        if (this.orderCreated === false && this.fromCheckout) {
          let time = firestoreTimestamp.fromDate(new Date());

          this.saveOrdersWithAdditionalCharge();

          // save amount in orders_total_by_table collection
          await this.addAmountOrderTotalByTable(time);

          // set total order quantity in localstorage
          this.saveMyOrders();

          // save general comments in firebase
          this.saveGeneralComment(time);

          this.showSuccessMessage(this.$t("cart.orderPlaced"));

          this.orderCreated = true;

          // create order user log
          this.setlogInfo(
            "create_order",
            "Pedido creado",
            [...this.cart],
            this.$options.name
          );

          // Update success: true to prevent using same URL to make other orders
          if (this.paymentId) {
            await updatePayment(this.paymentId, {
              success: true
            });
          }

          // clear everything from local storage
          await deleteAllUserCartByTable(this.tableQR, false);
          this.$store.commit("removeAllCart");
          window.localStorage.setItem("lastLand", "");

          this.$router.push("/receipt");
        }
      } catch (error) {
        this.setlogError(
          "update_payment",
          "Obtained error message in saveSuccessOrder()",
          error.stack,
          this.$options.name
        );
      }
    },
    saveOrdersWithAdditionalCharge() {
      let time = firestoreTimestamp.fromDate(new Date());
      let self = this;
      this.cart.forEach(item => {
        let id = self.uuidv4();
        saveOrder(id, self.buildOrder(item, time, id));
        self.total += item.isMenuGroupProduct
          ? item.price * item.quantity + this.totalWithAdditionalCharge(item)
          : item.price * item.quantity;
      });
    }
  }
};
</script>

<style scoped lang="scss"></style>
